import { useMutation, useQuery } from '@tanstack/react-query'
import fetch from '~/common/fetch'
import { queryOptions } from '~/common/react-query/queryOptions'
import { queryClient } from '~/common/react-query/queryClient'

export type AssistantThread = {
  id: number
  thread_id: string
  title: string
  updated_at: string
}

export type AssistantSource = {
  title: string
  url: string | null
}

export type AssistantThreadTurn = {
  id: string
  question: string
  answer: string | null
  sources: AssistantSource[]
  pending_run_id?: string
}

export type AssistantRunStatus = 'pending' | 'running' | 'success' | 'error' | 'timeout' | 'interrupted'

export type AssistantRun = {
  thread_id: string
  run_id: string
  status: AssistantRunStatus
  elapsed_seconds: number
  is_final: boolean
}

export type StartAssistantMessageResponse = {
  thread: AssistantThread
  run_id: string
}

export type AssistantRunResponse = {
  run: AssistantRun
  result: {
    answer: string | null
    sources: AssistantSource[]
    error: string | null
  } | null
}

export type StartAssistantMessageBody = {
  content: string
  thread_id?: string
}

export const ASSISTANT_THREADS_QUERY_KEY = ['assistant', 'threads'] as const

export const assistantThreadsOptions = () =>
  queryOptions({
    queryKey: ASSISTANT_THREADS_QUERY_KEY,
    queryFn: (): Promise<AssistantThread[]> => fetch.get('/api/v2/assistant/threads/'),
    keepPreviousData: true
  })

export const useAssistantThreadsQuery = () => useQuery(assistantThreadsOptions())

export const useStartAssistantMessageMutation = () =>
  useMutation({
    mutationFn: (body: StartAssistantMessageBody): Promise<StartAssistantMessageResponse> =>
      fetch.post('/api/v2/assistant/chat/messages/', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ASSISTANT_THREADS_QUERY_KEY })
  })

export const assistantRunOptions = (threadId: string | undefined, runId: string | undefined) =>
  queryOptions({
    queryKey: ['assistant', 'run', threadId, runId],
    queryFn: ({ signal }): Promise<AssistantRunResponse> =>
      fetch.get(`/api/v2/assistant/chat/threads/${threadId}/runs/${runId}/`, { signal }),
    enabled: !!threadId && !!runId,
    // Refetches come only from polling; a final run never changes.
    staleTime: Infinity,
    refetchInterval: (data, query) => (data?.run.is_final || query.state.status === 'error' ? false : 1000)
  })

export const assistantThreadTurnsOptions = (threadId: string | null) =>
  queryOptions({
    queryKey: ['assistant', 'turns', threadId],
    queryFn: ({ signal }): Promise<{ turns: AssistantThreadTurn[] }> =>
      fetch.get(`/api/v2/assistant/chat/threads/${threadId}/messages/`, { signal }),
    enabled: !!threadId,
    // Turns sent after loading are rendered locally, so a background refetch would duplicate them.
    // cacheTime 0 still gets fresh history whenever the thread is reopened.
    retry: false,
    staleTime: Infinity,
    cacheTime: 0
  })

export const useDeleteAssistantThreadMutation = () =>
  useMutation({
    mutationFn: (id: number): Promise<void> => fetch.delete(`/api/v2/assistant/threads/${id}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ASSISTANT_THREADS_QUERY_KEY })
  })
